import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Estado do backup automático no Google Drive (ver ADR 0027).
 *
 * Separado do `useAutoBackupStore` (SAF/pasta local, Android-only) de propósito: o Drive
 * tem identidade (conta Google) e folderId próprios, e não deve reaproveitar o URI de pasta
 * do SAF. Os dois podem coexistir.
 *
 * A cadência é FIXA em diária (`BACKUP_INTERVAL_DAYS`): a escolha diário/semanal virou
 * decisão de produto em vez de preferência — semanal deixava a janela de perda grande
 * demais numa feature paga de confiança, e ninguém a trocava conscientemente.
 */

interface DriveBackupState {
    /** Backup no Drive ligado (recurso Pro — só o LIGAR/automático é pago; restaurar é grátis). */
    enabled: boolean;
    /** E-mail da conta Google conectada (null = desconectado). Fonte da verdade do "conectado". */
    connectedEmail: string | null;
    /** Id da pasta "Kontto" no Drive do usuário (descoberto/criado sob demanda). */
    folderId: string | null;
    /** Quantas versões manter no Drive (rotação). */
    keepVersions: number;
    lastBackupAt: string | null;
    /** Última falha (pra observabilidade/CTA). null = último backup ok. */
    lastError: string | null;
    /** Quando o alerta de "backup obsoleto" foi mostrado por último (evita repetir). */
    staleAlertedAt: string | null;
    /**
     * Dica "Agora, no outro aparelho" do painel (ADR 0193): liga quando o assistente termina pelo
     * caminho do 1º aparelho e só desliga no X. Por aparelho; trocar de conta não a traz de volta.
     */
    otherDeviceTip: boolean;

    setEnabled: (enabled: boolean) => void;
    setConnection: (email: string | null, folderId?: string | null) => void;
    setFolder: (folderId: string | null) => void;
    markBackedUp: (at?: string) => void;
    setError: (error: string | null) => void;
    markStaleAlerted: (at?: string) => void;
    setOtherDeviceTip: (show: boolean) => void;
    reset: () => void;
}

const DEFAULTS = {
    enabled: false,
    connectedEmail: null as string | null,
    folderId: null as string | null,
    keepVersions: 5,
    lastBackupAt: null as string | null,
    lastError: null as string | null,
    staleAlertedAt: null as string | null,
    otherDeviceTip: false,
};

export const useDriveBackupStore = create<DriveBackupState>()(
    persist(
        (set) => ({
            ...DEFAULTS,
            setEnabled: (enabled) => set({ enabled }),
            setConnection: (connectedEmail, folderId) =>
                set(folderId === undefined ? { connectedEmail } : { connectedEmail, folderId }),
            setFolder: (folderId) => set({ folderId }),
            markBackedUp: (at) =>
                set({ lastBackupAt: at ?? new Date().toISOString(), lastError: null, staleAlertedAt: null }),
            setError: (lastError) => set({ lastError }),
            markStaleAlerted: (at) => set({ staleAlertedAt: at ?? new Date().toISOString() }),
            setOtherDeviceTip: (otherDeviceTip) => set({ otherDeviceTip }),
            // Desconectar zera conexão + estado de backup, mas NÃO o keepVersions (pref) nem a dica.
            reset: () =>
                set({
                    enabled: false,
                    connectedEmail: null,
                    folderId: null,
                    lastBackupAt: null,
                    lastError: null,
                    staleAlertedAt: null,
                }),
        }),
        {
            name: '@finapp_drive_backup',
            storage: createJSONStorage(() => AsyncStorage),
        },
    ),
);

export async function hydrateDriveBackupStore(): Promise<void> {
    try {
        if (useDriveBackupStore.persist.hasHydrated()) return;
        await useDriveBackupStore.persist.rehydrate();
    } catch {
        // Mantém os defaults se o AsyncStorage falhar.
    }
}
